import { Link2, Unlink2, Zap, X } from 'lucide-react';
import type {
  CanvasElement,
  ShapeElement,
  ImageElement,
  ButtonElement,
  SvgElement,
  FillConfig,
  BorderConfig,
} from '../../types';
import {
  resolveElementFill,
  resolveElementBorder,
  resolveElementShadow,
  defaultFill,
} from '../../lib/elementDefaults';
import ColorPicker from './ColorPicker';
import ShadowEditor from './ShadowEditor';
import SvgColorEditor from './SvgColorEditor';
import { useMappingStore } from '../../stores/mappingStore';
import type { EnhancedFieldMapping, MappableProperty } from '../../types/mapping';

interface AppearanceSectionProps {
  element: CanvasElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
  onUpdateElement?: (id: string, updates: Partial<CanvasElement>) => void;
}

function MappingBadge({
  mapping,
  onUnlink,
}: {
  mapping: EnhancedFieldMapping;
  onUnlink: () => void;
}) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
      <Zap size={11} className="text-cyan-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] text-cyan-300 font-medium truncate">
          {mapping.tableName}.{mapping.columnName}
        </div>
        <div className="text-[9px] text-zinc-500 truncate">
          Dynamic -- unlink to edit
        </div>
      </div>
      <button
        onClick={onUnlink}
        className="p-0.5 rounded hover:bg-cyan-500/20 text-cyan-400 hover:text-cyan-300 transition-colors shrink-0"
        title="Unlink mapping"
      >
        <X size={11} />
      </button>
    </div>
  );
}

const EMPTY_MAPPINGS: EnhancedFieldMapping[] = [];

export default function AppearanceSection({ element, onUpdate, onUpdateElement }: AppearanceSectionProps) {
  const elementMappings = useMappingStore((s) => s.elementMappings[element.id] ?? EMPTY_MAPPINGS);
  const removeMapping = useMappingStore((s) => s.removeMapping);

  const findMapping = (property: MappableProperty): EnhancedFieldMapping | undefined =>
    elementMappings.find((m) => m.targetProperty === property);

  const fillMapping = findMapping('fillColor');
  const strokeMapping = findMapping('strokeColor');
  const fontColorMapping = findMapping('fontColor');
  const fill = resolveElementFill(element as unknown as Record<string, unknown>);
  const border = resolveElementBorder(element as unknown as Record<string, unknown>);
  const shadow = resolveElementShadow(element as unknown as Record<string, unknown>);

  const isShape = element.type === 'shape';
  const isImage = element.type === 'image';
  const isButton = element.type === 'button';
  const isText = element.type === 'text';
  const isSvg = element.type === 'svg';

  const handleFillChange = (newFill: FillConfig) => {
    if (isShape) {
      const updates: Record<string, unknown> = { shapeFill: newFill };
      if (newFill.type === 'solid') updates.fill = newFill.color;
      onUpdate(updates as Partial<CanvasElement>);
    } else if (isButton) {
      const updates: Record<string, unknown> = { buttonFill: newFill };
      if (newFill.type === 'solid') updates.backgroundColor = newFill.color;
      onUpdate(updates as Partial<CanvasElement>);
    } else if (isText) {
      const updates: Record<string, unknown> = { textFill: newFill };
      if (newFill.type === 'solid') updates.color = newFill.color;
      onUpdate(updates as Partial<CanvasElement>);
    }
  };

  const handleBorderUpdate = (partial: Partial<BorderConfig>) => {
    const updated = { ...border, ...partial };
    if (partial.radius) {
      updated.radius = { ...(border.radius || { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0, linked: true }), ...partial.radius };
    }
    const updates: Record<string, unknown> = { border: updated };
    if (partial.width !== undefined) updates.strokeWidth = partial.width;
    if (partial.color !== undefined) updates.stroke = partial.color;
    onUpdate(updates as Partial<CanvasElement>);
  };

  return (
    <div className="space-y-4">
      {!isImage && !isSvg && (
        <div className="space-y-2">
          {(isText ? fontColorMapping : fillMapping) && (
            <MappingBadge
              mapping={(isText ? fontColorMapping : fillMapping) as EnhancedFieldMapping}
              onUnlink={() =>
                removeMapping(
                  element.id,
                  ((isText ? fontColorMapping : fillMapping) as EnhancedFieldMapping).id
                )
              }
            />
          )}
          {!(isText ? fontColorMapping : fillMapping) && (
            <ColorPicker
              fill={fill}
              onChange={handleFillChange}
              showGradient={true}
              label="Fill"
              showOpacity={true}
              opacity={element.opacity}
              onOpacityChange={(opacity) => onUpdate({ opacity } as Partial<CanvasElement>)}
            />
          )}
          {(isText ? fontColorMapping : fillMapping) && (
            <OpacitySlider
              value={element.opacity}
              onChange={(opacity) => onUpdate({ opacity } as Partial<CanvasElement>)}
            />
          )}
        </div>
      )}

      {isSvg && onUpdateElement && (
        <SvgColorEditor
          element={element as SvgElement}
          onUpdateElement={onUpdateElement}
        />
      )}

      {(isImage || isSvg) && (
        <OpacitySlider
          value={element.opacity}
          onChange={(opacity) => onUpdate({ opacity } as Partial<CanvasElement>)}
        />
      )}

      <ShadowEditor
        shadow={shadow}
        onChange={(newShadow) => onUpdate({ shadow: newShadow } as Partial<CanvasElement>)}
      />

      {(isShape || isImage) && (
        <BlurControl
          value={(element as ShapeElement | ImageElement).blur ?? 0}
          onChange={(blur) => onUpdate({ blur } as Partial<CanvasElement>)}
        />
      )}

      {(isShape || isButton) && (
        <BorderControls
          border={border}
          onUpdate={handleBorderUpdate}
          strokeMapping={strokeMapping}
          onUnlinkStroke={
            strokeMapping ? () => removeMapping(element.id, strokeMapping.id) : undefined
          }
        />
      )}

      {(isShape || isButton) && (
        <BorderRadiusControl
          border={border}
          onUpdate={(radius) => {
            const uniformVal = radius.linked ? radius.topLeft : 0;
            const updates: Record<string, unknown> = {
              border: { ...border, radius },
              borderRadius: radius.linked ? radius.topLeft : uniformVal,
            };
            onUpdate(updates as Partial<CanvasElement>);
          }}
        />
      )}

      {isImage && (
        <ImageAdjustments
          element={element as ImageElement}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

function OpacitySlider({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Opacity</label>
        <span className="text-[10px] text-zinc-400 tabular-nums font-mono">
          {Math.round(value * 100)}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(parseInt(e.target.value) / 100)}
        className="w-full"
      />
    </div>
  );
}

function BlurControl({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">Blur</label>
        <span className="text-[10px] text-zinc-400 tabular-nums font-mono">
          {value}px
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={20}
        step={0.5}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full"
      />
    </div>
  );
}

function BorderControls({
  border,
  onUpdate,
  strokeMapping,
  onUnlinkStroke,
}: {
  border: BorderConfig;
  onUpdate: (partial: Partial<BorderConfig>) => void;
  strokeMapping?: EnhancedFieldMapping;
  onUnlinkStroke?: () => void;
}) {
  return (
    <div className="space-y-2.5">
      <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
        Border
      </label>

      <div className="flex items-center gap-2">
        <label className="text-[10px] text-zinc-500 w-12 shrink-0">Width</label>
        <input
          type="number"
          min={0}
          max={20}
          value={border.width}
          onChange={(e) => onUpdate({ width: parseFloat(e.target.value) || 0 })}
          className="flex-1 px-2 py-1.5 text-xs bg-[#0a0a0a] border border-white/10 rounded-lg text-zinc-200 text-center focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
        />
        <span className="text-[9px] text-zinc-600">px</span>
      </div>

      {border.width > 0 && (
        <>
          {strokeMapping && onUnlinkStroke ? (
            <MappingBadge mapping={strokeMapping} onUnlink={onUnlinkStroke} />
          ) : (
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-zinc-500 w-12 shrink-0">Color</label>
              <input
                type="color"
                value={border.color.startsWith('#') ? border.color.slice(0, 7) : '#ffffff'}
                onChange={(e) => onUpdate({ color: e.target.value })}
                className="w-7 h-7 rounded border border-white/10 cursor-pointer bg-transparent"
              />
              <input
                type="text"
                value={border.color}
                onChange={(e) => onUpdate({ color: e.target.value })}
                className="flex-1 px-2 py-1.5 text-xs bg-[#0a0a0a] border border-white/10 rounded-lg text-zinc-200 font-mono focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <label className="text-[10px] text-zinc-500 w-12 shrink-0">Style</label>
            <div className="flex gap-1 flex-1">
              {(['solid', 'dashed', 'dotted'] as const).map((style) => (
                <button
                  key={style}
                  onClick={() => onUpdate({ style })}
                  className={`flex-1 px-2 py-1.5 text-[10px] rounded-md border transition-colors ${
                    border.style === style
                      ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400'
                      : 'bg-[#0a0a0a] border-white/[0.06] text-zinc-500 hover:border-white/[0.1]'
                  }`}
                >
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BorderRadiusControl({
  border,
  onUpdate,
}: {
  border: BorderConfig;
  onUpdate: (radius: NonNullable<BorderConfig['radius']>) => void;
}) {
  const radius = border.radius || { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0, linked: true };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">
          Corner Radius
        </label>
        <button
          onClick={() => onUpdate({ ...radius, linked: !radius.linked })}
          className={`p-1 rounded transition-colors ${
            radius.linked
              ? 'text-cyan-400 bg-cyan-500/10'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
          title={radius.linked ? 'Linked -- all corners change together' : 'Unlinked -- individual corner control'}
        >
          {radius.linked ? <Link2 size={12} /> : <Unlink2 size={12} />}
        </button>
      </div>

      {radius.linked ? (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={100}
            value={radius.topLeft}
            onChange={(e) => {
              const r = parseInt(e.target.value);
              onUpdate({ topLeft: r, topRight: r, bottomRight: r, bottomLeft: r, linked: true });
            }}
            className="flex-1"
          />
          <span className="text-[10px] text-zinc-400 tabular-nums font-mono w-8 text-right">
            {radius.topLeft}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {(['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const).map((corner) => (
            <div key={corner} className="flex items-center gap-1">
              <label className="text-[9px] text-zinc-600 w-5 shrink-0">
                {corner === 'topLeft' ? 'TL' : corner === 'topRight' ? 'TR' : corner === 'bottomLeft' ? 'BL' : 'BR'}
              </label>
              <input
                type="number"
                min={0}
                max={200}
                value={radius[corner]}
                onChange={(e) => {
                  onUpdate({ ...radius, [corner]: parseInt(e.target.value) || 0, linked: false });
                }}
                className="flex-1 px-1.5 py-1 text-xs bg-[#0a0a0a] border border-white/10 rounded-lg text-zinc-200 text-center focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageAdjustments({
  element,
  onUpdate,
}: {
  element: ImageElement;
  onUpdate: (updates: Partial<CanvasElement>) => void;
}) {
  const adjustments = [
    { key: 'brightness' as const, label: 'Brightness' },
    { key: 'contrast' as const, label: 'Contrast' },
    { key: 'saturation' as const, label: 'Saturation' },
  ];

  const hasAdjustments = adjustments.some((a) => (element[a.key] ?? 0) !== 0);

  return (
    <div className="space-y-2.5">
      <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block">
        Image Adjustments
      </label>

      {adjustments.map(({ key, label }) => {
        const value = element[key] ?? 0;
        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[10px] text-zinc-500">{label}</label>
              <span className="text-[10px] text-zinc-400 tabular-nums font-mono">
                {Math.round(value * 100)}
              </span>
            </div>
            <input
              type="range"
              min={-100}
              max={100}
              value={Math.round(value * 100)}
              onChange={(e) => onUpdate({ [key]: parseInt(e.target.value) / 100 } as Partial<CanvasElement>)}
              className="w-full"
            />
          </div>
        );
      })}

      {hasAdjustments && (
        <button
          onClick={() => onUpdate({ brightness: 0, contrast: 0, saturation: 0 } as Partial<CanvasElement>)}
          className="w-full px-2 py-1.5 text-[10px] text-zinc-500 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:text-zinc-300 hover:border-white/[0.1] transition-colors"
        >
          Reset Adjustments
        </button>
      )}
    </div>
  );
}
